import type { FC, Ref } from "react";
import styles from "./CustomSelect.module.css";
import { ChevronLeftIcon } from "@/components/Icons";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectViewport,
  SelectItemText,
  SelectPortal,
} from "@radix-ui/react-select";
import { FieldError } from "react-hook-form";
import { useTranslation } from "@/i18n";

export interface Option {
  value: string;
  label: string;
  className?: string;
}

interface SelectProps {
  options: Option[];
  name?: string;
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  ref?: Ref<HTMLButtonElement>;
  error?: FieldError;
}

export const CustomSelect: FC<SelectProps> = ({
  options,
  name,
  value,
  defaultValue,
  onChange,
  disabled = false,
  ref,
  error,
}) => {
  const { t } = useTranslation();
  const negativeOptions = options
    .filter((opt) => opt.value.startsWith("-"))
    .sort((a, b) => {
      const numA = parseFloat(a.value);
      const numB = parseFloat(b.value);
      return numB - numA;
    });
  const positiveOptions = options.filter(
    (opt) =>
      !opt.value.startsWith("-") &&
      (opt.value !== "+0.00" || negativeOptions.length === 0),
  );

  const zeroOption = options.find((opt) => opt.value === "+0.00");

  const valueToDisplay = (() => {
    if (!value || value === "") {
      return t("steps.advancedPrescription.noValue");
    }
    const selectedOption = options.find((opt) => opt.value === value);
    if (!selectedOption) {
      return value;
    }
    if (name?.includes("CL_AXIS")) {
      return selectedOption.label.startsWith("0")
        ? selectedOption.label.slice(1) + t("common.degSign")
        : selectedOption.label + t("common.degSign");
    }
    if (selectedOption.value === "+0.00") {
      return "0.00";
    }
    return selectedOption.label;
  })();

  return (
    <Select
      name={name}
      value={value}
      onValueChange={onChange}
      defaultValue={defaultValue}
      disabled={disabled}
    >
      <SelectTrigger
        className={`${styles.selectTrigger} ${error && styles.error}`}
        ref={ref}
      >
        <p className={styles.value}>{valueToDisplay}</p>

        <ChevronLeftIcon size={20} color="text" className={styles.chevron} />
      </SelectTrigger>
      <SelectPortal>
        <SelectContent
          className={`cl-configurator ${styles.options}`}
          position="popper"
          side="bottom"
          style={{ zIndex: 999999 }}
        >
          <SelectViewport>
            {negativeOptions.length > 0 && positiveOptions.length > 0 && (
              <div className={styles.signs}>
                <div
                  className={`${styles.option} ${styles.firstOption} ${positiveOptions.length < negativeOptions.length ? styles.borderRight : ""}`}
                >
                  -
                </div>
                <div
                  className={`${styles.option} ${styles.firstOption} ${positiveOptions.length < negativeOptions.length ? "" : styles.borderLeft}`}
                >
                  +
                </div>
              </div>
            )}
            {negativeOptions.length > 0 &&
              positiveOptions.length > 0 &&
              zeroOption && (
                <Option
                  key={zeroOption.value}
                  value={zeroOption.value}
                  label={zeroOption.label.slice(1)}
                />
              )}
            <div className={styles.viewport}>
              {negativeOptions.length > 0 && (
                <>
                  <SelectGroup className={styles.optionGroup}>
                    {negativeOptions.map((opt) => (
                      <Option
                        key={opt.value}
                        value={opt.value}
                        label={opt.label}
                        className={
                          positiveOptions.length < negativeOptions.length
                            ? styles.border
                            : ""
                        }
                      />
                    ))}
                  </SelectGroup>
                </>
              )}
              {positiveOptions.length > 0 && (
                <SelectGroup className={styles.optionGroup}>
                  {positiveOptions.map((opt) => {
                    if (name?.includes("CL_AXIS")) {
                      const optLabel = opt.label.startsWith("0")
                        ? opt.label.slice(1)
                        : opt.label;
                      return (
                        <Option
                          key={opt.value}
                          value={opt.value}
                          label={optLabel + t("common.degSign")}
                          className={
                            positiveOptions.length < negativeOptions.length
                              ? ""
                              : styles.border
                          }
                        />
                      );
                    } else {
                      return (
                        <Option
                          key={opt.value}
                          value={opt.value}
                          label={opt.label}
                          className={
                            positiveOptions.length < negativeOptions.length
                              ? ""
                              : styles.border
                          }
                        />
                      );
                    }
                  })}
                </SelectGroup>
              )}
            </div>
          </SelectViewport>
        </SelectContent>
      </SelectPortal>
    </Select>
  );
};

const Option = ({ value, label, className }: Option) => (
  <SelectItem
    key={value}
    value={value}
    className={styles.option + " " + className}
  >
    <SelectItemText>{label}</SelectItemText>
  </SelectItem>
);

export default CustomSelect;
